import { observable, toJS, flow, action } from "mobx";
import {
	getArticle,
	downloadRankerXArticle,
	downloadArticleBuilderArticle,
	downloadArticleForgeArticle,
	downloadUSAArticle,
	saveArticle,
	getSampleArticle,
	spinArticle
} from "services/contentgroups";
import { registerHandler, delay } from "services/api";
import { message } from "antd";
import _ from "lodash";
import { Spin } from "common/utils";

export default class ArticleStore {
	constructor() {
		this.ui = this.getUI();
	}

	@observable
	article = {};

	@observable
	contentGroup = {};

	@observable
	articleBuilderCategories = [];

	getUI = () => {
		let ui = {
			articleChanged: false,
			savingArticle: false,
			loadingArticle: false,
			rankerXArticleDrawerVisible: false,
			articleBuilderDrawerVisible: false,
			articleForgeDrawerVisible: false,
			usaDrawerVisible: false,
			downloadingRankerXArticle: false,
			downloadingArticleBuilderArticle: false,
			downloadingArticleForgeArticle: false,
			downloadingUSAArticle: false,
			spinningArticle: false,
			previewArticleDrawerVisible: false,
			articleText: ""
		};
		return observable.object(ui);
	};

	@action
	formChanged = fields => {	
		// console.log(fields);
		let customizer = (objValue, srcValue, key, object, source, stack) => {
			if (srcValue == undefined) {
				return null;
			}
		};
		_.mergeWith(this.stateForm, fields, customizer);
		this.ui.articleChanged = true;
	};

	getArticle = () => {
		const article = this.stateForm;

		const title = Spin(article.title.value);		

		let body = Spin(article.body.value);
		if (body) {
			body = body.replace(/\n/g, "<br/>");
		}

		const authorName = Spin(article.authorName.value);
		const authorBio = Spin(article.authorBio.value);
		
		const articleText = 
			`<b>${title}</b><br/>
				${body}<br/>
				<b>Name: </b>${authorName}<br/>
				<b>Bio:</b><br/>${authorBio}`;
		
		return articleText;
	};

	@action
	refreshArticleText = () => {
		this.ui.articleText = this.getArticle();
	}

	@action
	showPreviewArticleDrawer = () => {
		this.ui.previewArticleDrawerVisible = true;
		this.ui.articleText = this.getArticle();
	}

	@action
	showRankerXArticleDrawer = () => {
		this.ui.rankerXArticleDrawerVisible = true;
	};

	@action
	showArticleBuilderDrawer = () => {
		this.ui.articleBuilderDrawerVisible = true;
	};

	@action
	showArticleForgeDrawer = () => {
		this.ui.articleForgeDrawerVisible = true;
	};

	@action
	showUSADrawer = () => {
		this.ui.usaDrawerVisible = true;
	};

	@action
	hidePreviewArticleDrawer = () => {
		this.ui.previewArticleDrawerVisible = false;
	}

	@action
	hideRankerXArticleDrawer = () => {
		this.ui.rankerXArticleDrawerVisible = false;
	};

	@action
	hideArticleBuilderDrawer = () => {
		this.ui.articleBuilderDrawerVisible = false;
	};

	@action
	hideArticleForgeDrawer = () => {
		this.ui.articleForgeDrawerVisible = false;
	};

	@action
	hideUSADrawer = () => {
		this.ui.usaDrawerVisible = false;
	};

	spinArticle = flow(function*(spinnerService) {
		let title = this.stateForm.title.value;
		let body = this.stateForm.body.value;
		this.ui.spinningArticle = true;
		yield delay(300);
		const response = yield spinArticle(this.contentGroup.id, {
			spinnerService: spinnerService,
			title: title,
			body: body
		});

		this.ui.spinningArticle = false;

		if (response == null) {
			message.error("Failed to spin the article. Unknown error", 5);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully spun the article", 5);
		this.ui.articleChanged = true;
		this.stateForm.title.value = response.title;
		this.stateForm.body.value = response.body;
	});

	createFormObject = object => {
		function customizer(value, key) {
			if (!_.isObject(value) && key !== "value") {
				return {
					value: value
				};
			}
			if (_.isArray(value)) {
				return _.cloneDeep(value);
			}
		}

		let newObj = _.cloneDeepWith(toJS(object), customizer);
		return newObj;
	};

	getRawObjectFromFormObject = formObject => {
		function customizer(value) {
			if (_.isObject(value)) {
				if ("value" in value) {
					return value.value;
				}
			}
		}
		let rawJS = toJS(formObject);
		return _.cloneDeepWith(rawJS, customizer);
	};

	downloadArticleForgeArticle = flow(function*(payload) {		
		this.ui.downloadingArticleForgeArticle = true;
		const response = yield downloadArticleForgeArticle(payload);
		this.ui.downloadingArticleForgeArticle = false;
		if (response == null) {
			message.error("Failed to download Article Forge Article. Unknown error", 5);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully download Article Forge Article", 5);
		this.ui.articleChanged = true;
		this.ui.articleForgeDrawerVisible = false;		
		this.stateForm.title.value = response.article.title;
		this.stateForm.title.errors = null;
		this.stateForm.body.value = response.article.body;
		this.stateForm.body.errors = null;
		return;
	});

	downloadRankerXArticle = flow(function*(payload) {		
		this.ui.downloadingRankerXArticle = true;
		const response = yield downloadRankerXArticle(payload);
		this.ui.downloadingRankerXArticle = false;
		if (response == null) {
			message.error("Failed to download built-in articles. Unknown error", 5);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully download built-in articles", 5);
		this.ui.articleChanged = true;
		this.ui.rankerXArticleDrawerVisible = false;
		// console.log(this.stateForm);
		this.stateForm.title.value = response.article.title;
		this.stateForm.title.errors = null;
		this.stateForm.body.value = response.article.body;
		this.stateForm.body.errors = null;
		return;
	});

	downloadArticleBuilderArticle = flow(function*(payload) {
		this.ui.downloadingArticleBuilderArticle = true;
		const response = yield downloadArticleBuilderArticle(payload);
		this.ui.downloadingArticleBuilderArticle = false;
		if (response == null) {
			message.error(
				"Failed to download Article Builder articles. Unknown error",
				5
			);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully download Article Builder articles", 5);
		this.ui.articleChanged = true;
		this.ui.articleBuilderDrawerVisible = false;
		this.stateForm.title.value = response.article.title;
		this.stateForm.title.errors = null;
		this.stateForm.body.value = response.article.body;
		this.stateForm.body.errors = null;
		return;
	});

	downloadUSAArticle = flow(function*(payload) {
		this.ui.downloadingUSAArticle = true;
		const response = yield downloadUSAArticle(payload);
		this.ui.downloadingUSAArticle = false;
		if (response == null) {
			message.error(
				"Failed to download Ultra Spinnable Article. Unknown error",
				5
			);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully download Ultra Spinnable Article", 5);
		this.ui.articleChanged = true;
		this.ui.usaDrawerVisible = false;
		this.stateForm.title.value = response.article.title;
		this.stateForm.title.errors = null;
		this.stateForm.body.value = response.article.body;
		this.stateForm.body.errors = null;
		return;
	});

	saveArticle = flow(function*() {
		let article = this.getRawObjectFromFormObject(this.stateForm);
		this.ui.savingArticle = true;
		yield delay(300);
		const response = yield saveArticle(article);
		this.ui.savingArticle = false;
		if (response == null) {
			message.error("Failed to save the article. Unknown error", 5);
			return;
		}
		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}
		//In case of creating a new article
		article.id = response.id;
		this.stateForm.id.value = response.id;
		this.article = observable.object(article);
		message.success("Successfully saved the article", 5);
		this.ui.articleChanged = false;
		return;
	});

	initArticle = flow(function*(contentGroupId) {
		this.ui = this.getUI();
		this.ui.loadingArticle = true;
		const response = yield getSampleArticle(contentGroupId);

		if (response == null) {
			message.error("Failed to load the article. Unexpected error", 3);
			return;
		}
		if (response.error) {
			message.error(response.error.message, 3);
			return;
		}

		this.ui.loadingArticle = false;
		this.article = observable.object(response.article);
		this.contentGroup = observable.object(response.contentGroup);
		this.articleBuilderCategories = observable.array(
			JSON.parse(response.articleBuilderCategories)
		);
		this.stateForm = observable.object(this.createFormObject(this.article));
		// console.log(this.stateForm);
	});

	loadArticle = flow(function*(articleId) {
		this.ui = this.getUI();
		this.ui.loadingArticle = true;
		const response = yield getArticle(articleId);

		if (response == null) {
			message.error("Failed to load the article. Unexpected error", 3);
			return;
		}
		if (response.error) {
			message.error(response.error.message, 3);
			return;
		}

		this.ui.loadingArticle = false;
		this.article = observable.object(response.article);
		this.contentGroup = observable.object(response.contentGroup);
		this.articleBuilderCategories = observable.array(
			JSON.parse(response.articleBuilderCategories)
		);
		this.stateForm = observable.object(this.createFormObject(this.article));

		// registerHandler([
		// 	{
		// 		path: "campaigns",
		// 		handler: (err, msg) => {
		// 			if (err != null) return;
		// 			const obj = JSON.parse(msg.body);
		// 			if (obj.action == "addNew") {
		// 				var campaign = obj.campaign;
		// 				this.addCampaign(campaign);
		// 				return;
		// 			}

		// 			if (obj.action == "rename") {
		// 				this.renameCampaign({
		// 					name: obj.name,
		// 					id: obj.id
		// 				});
		// 				return;
		// 			}

		// 			if (obj.action == "campaignsDeleted") {
		// 				this.deleteCampaigns(obj.ids);
		// 				return;
		// 			}

		// 			if (obj.action == "statusChanged") {
		// 				this.changeCampaignStatus(obj.campaign);
		// 				return;
		// 			}
		// 		}
		// 	}
		// ]);
	});
}
